/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { createAsyncThunk } from "@reduxjs/toolkit";
import isEmpty from "lodash/fp/isEmpty";
import keys from "lodash/fp/keys";

import ReportApi from "~/features/reportTracker/ReportApi";
import { ReportRequest } from "~/features/reportTracker/types";
import StatisticsApi from "~/features/statistics/StatisticsApi";
import transformToTotalStatsSite from "~/features/statistics/transformers/transformToTotalStatsSite";
import transformToTotalStatsTotals from "~/features/statistics/transformers/transformToTotalStatsTotals";
import { PostEmailSubscriptionPayload } from "~/features/statistics/types";
import mapReportData from "~/features/statistics/utils/mapReportData";

export const loadSitesAsync = createAsyncThunk("statistics/loadSites", async () => {
  const [referrerRes, promotionRes] = await Promise.all([
    StatisticsApi.getReferrerSites(),
    StatisticsApi.getPromotionSites(),
  ]);

  const referrerSitesById = referrerRes.data.sites;
  const promotionsSitesById = promotionRes.data.promotions.reduce(
    (byId, item) => {
      byId[item.siteid] = item.domain;
      return byId;
    },
    {} as Record<number, string>,
  );

  const byId = {
    ...referrerSitesById,
    ...promotionsSitesById,
  };

  const referrerSitesByName = Object.fromEntries(
    Object.entries(referrerRes.data.sites).map(([key, value]) => [value, key]),
  ) as unknown as Record<string, number>;

  const promotionSitesByName = promotionRes.data.promotions.reduce(
    (byName, item) => {
      byName[item.domain] = item.siteid;
      return byName;
    },
    {} as Record<string, number>,
  );

  const byName = {
    ...referrerSitesByName,
    ...promotionSitesByName,
  };

  const sortedIds = keys(byId)
    .map(Number)
    .sort((a, b) => byId[a].localeCompare(byId[b]));

  return {
    byId,
    sortedIds,
    byName,
  };
});

export const loadReportAsync = createAsyncThunk("report/postGetReport", async (payload: ReportRequest) => {
  try {
    const res = await ReportApi.postGetReport(payload);
    const { result } = res.data;

    return mapReportData(result);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-member-access
    throw new Error((error as any).response.status);
  }
});

export const autoLoadReportAsync = createAsyncThunk("report/postGetAutomaticReport", async (payload: ReportRequest) => {
  const res = await ReportApi.postGetAutomaticReport(payload);
  const { totals, body } = res.data.result;

  return {
    totals: !isEmpty(totals) ? transformToTotalStatsTotals(totals) : [],
    sites: !isEmpty(body) ? body.map(transformToTotalStatsSite) : [],
  };
});

export const getEmailSubscriptionAsync = createAsyncThunk("statistics/getEmailSubscription", async () => {
  const res = await StatisticsApi.getEmailSubscription();

  return res.data.result;
});

export const sendEmailSubscriptionAsync = createAsyncThunk(
  "statistics/sendEmailSubscription",
  async (payload: PostEmailSubscriptionPayload) => {
    await StatisticsApi.postEmailSubscription(payload);
  },
);
